import { BrowserRenderingError } from "./browser-run";

/**
 * A Browser Run binding that supports [Quick Actions](https://developers.cloudflare.com/browser-run/quick-actions/).
 *
 * This is the `quickAction()` surface of the `BrowserRun` binding generated by
 * `wrangler types`. Quick Actions are stateless one-shot tasks (screenshot,
 * PDF, markdown, structured extraction, …) — no session, no token, just the
 * binding.
 *
 * Requires a Worker `compatibility_date` of `2026-03-24` or later, and
 * `remote: true` on the browser binding for local `wrangler dev`.
 */
export interface QuickActionBinding {
  // `options: unknown` (declared as a method, so parameters are checked
  // bivariantly) lets the real `BrowserRun` binding — whose `quickAction` is a
  // set of per-action typed overloads — satisfy this structural type.
  quickAction(action: string, options: unknown): Promise<Response>;
}

/** The Quick Action endpoints exposed by Browser Run. */
export type QuickAction =
  | "content"
  | "screenshot"
  | "pdf"
  | "markdown"
  | "snapshot"
  | "scrape"
  | "json"
  | "links"
  | "crawl";

type GotoOptions = {
  waitUntil?: "load" | "domcontentloaded" | "networkidle0" | "networkidle2";
  timeout?: number;
};

/** Options accepted by every Quick Action endpoint. */
export interface QuickActionCommonOptions {
  gotoOptions?: GotoOptions;
  viewport?: { width: number; height: number; deviceScaleFactor?: number };
  cookies?: unknown[];
  authenticate?: { username: string; password: string };
  setExtraHTTPHeaders?: Record<string, string>;
  userAgent?: string;
  /** Regex strings for requests to drop before render (e.g. `["/\\.css$/"]`). */
  rejectRequestPattern?: string[];
}

/**
 * The page a Quick Action operates on: a `url` to load, or raw `html` to
 * render. Exactly one is required.
 */
export type QuickActionPage =
  | ({ url: string; html?: never } & QuickActionCommonOptions)
  | ({ html: string; url?: never } & QuickActionCommonOptions);

/** Input for the AI-powered `/json` extraction endpoint. */
export type QuickActionExtractInput = QuickActionPage & {
  /** Natural-language instruction for what to extract. */
  prompt?: string;
  /** A JSON Schema describing the desired output shape. */
  response_format?: { type: "json_schema"; json_schema: unknown };
  /** Bring-your-own model(s), tried in order, for extraction. */
  custom_ai?: { model: string; authorization: string }[];
};

/** Input for the `/scrape` endpoint. */
export type QuickActionScrapeInput = QuickActionPage & {
  elements: { selector: string }[];
};

/** Input for the `/screenshot` endpoint. */
export type QuickActionScreenshotInput = QuickActionPage & {
  screenshotOptions?: Record<string, unknown>;
  selector?: string;
};

/** Binary output (screenshot or PDF) with its reported content type. */
export interface QuickActionBinary {
  data: Uint8Array;
  contentType: string;
}

/** Any Quick Action input accepted by the typed helpers. */
export type QuickActionInput =
  | QuickActionPage
  | QuickActionExtractInput
  | QuickActionScrapeInput
  | QuickActionScreenshotInput;

/** Elements matched by one selector via `/scrape`. */
export interface QuickActionScrapeResult {
  selector: string;
  results: {
    html: string;
    text: string;
    width: number;
    height: number;
    top: number;
    left: number;
    attributes: { name: string; value: string }[];
  }[];
}

/** Result of a `/snapshot` request: rendered HTML plus a base64 screenshot. */
export interface QuickActionSnapshot {
  /** HTML content of the page. */
  content: string;
  /** Base64-encoded screenshot image. */
  screenshot: string;
}

/**
 * Run a Quick Action and return the raw {@link Response} from the binding.
 *
 * Most callers want a typed helper ({@link browserMarkdown},
 * {@link browserExtract}, …) instead; reach for this only when you need an
 * endpoint or option not yet wrapped. The per-action overloads type `params`
 * against the action when it is a string literal (e.g. `"json"` requires a
 * {@link QuickActionExtractInput}); any {@link QuickActionInput} is accepted
 * otherwise.
 */
export function runQuickAction(
  browser: QuickActionBinding,
  action: "json",
  params: QuickActionExtractInput
): Promise<Response>;
export function runQuickAction(
  browser: QuickActionBinding,
  action: "scrape",
  params: QuickActionScrapeInput
): Promise<Response>;
export function runQuickAction(
  browser: QuickActionBinding,
  action: "screenshot",
  params: QuickActionScreenshotInput
): Promise<Response>;
export function runQuickAction(
  browser: QuickActionBinding,
  action: QuickAction,
  params: QuickActionInput
): Promise<Response>;
export function runQuickAction(
  browser: QuickActionBinding,
  action: QuickAction,
  params: QuickActionInput
): Promise<Response> {
  return browser.quickAction(action, params);
}

/** Build an error that includes the service's explanation, if the body has one. */
async function quickActionError(
  action: QuickAction,
  response: Response
): Promise<BrowserRenderingError> {
  let detail = "";
  try {
    const text = await response.text();
    try {
      const body = JSON.parse(text) as {
        errors?: { message?: string }[];
        rawAiResponse?: string;
      };
      const messages = (body.errors ?? [])
        .map((e) => e.message)
        .filter((m): m is string => Boolean(m));
      if (messages.length > 0) detail = `: ${messages.join("; ")}`;
      else if (body.rawAiResponse) detail = `: ${body.rawAiResponse}`;
      else if (text) detail = `: ${text.slice(0, 200)}`;
    } catch {
      if (text) detail = `: ${text.slice(0, 200)}`;
    }
  } catch {
    // Body unreadable — fall back to the status code alone.
  }
  return new BrowserRenderingError(
    `Browser Run ${action} failed (${response.status})${detail}`,
    response.status
  );
}

async function quickActionJson<T>(
  browser: QuickActionBinding,
  action: QuickAction,
  params: QuickActionInput
): Promise<T> {
  const response = await runQuickAction(browser, action, params);
  if (!response.ok) throw await quickActionError(action, response);
  const payload = (await response.json()) as {
    success?: boolean;
    result?: T;
    errors?: { message?: string }[];
  };
  if (payload.success === false || payload.result === undefined) {
    const messages = (payload.errors ?? [])
      .map((e) => e.message)
      .filter((m): m is string => Boolean(m));
    throw new BrowserRenderingError(
      `Browser Run ${action} returned no result${messages.length > 0 ? `: ${messages.join("; ")}` : ""}`,
      response.status
    );
  }
  return payload.result;
}

async function quickActionBinary(
  browser: QuickActionBinding,
  action: QuickAction,
  params: QuickActionInput
): Promise<QuickActionBinary> {
  const response = await runQuickAction(browser, action, params);
  if (!response.ok) throw await quickActionError(action, response);
  const buffer = await response.arrayBuffer();
  return {
    data: new Uint8Array(buffer),
    contentType:
      response.headers.get("content-type") ?? "application/octet-stream"
  };
}

/** Fetch a page's fully rendered HTML via `/content`. */
export function browserContent(
  browser: QuickActionBinding,
  input: QuickActionPage
): Promise<string> {
  return quickActionJson<string>(browser, "content", input);
}

/** Convert a page (or raw HTML) to Markdown via `/markdown`. */
export function browserMarkdown(
  browser: QuickActionBinding,
  input: QuickActionPage
): Promise<string> {
  return quickActionJson<string>(browser, "markdown", input);
}

/**
 * Extract structured data from a page with AI via `/json`. Provide a `prompt`,
 * a `response_format` JSON Schema, or both.
 */
export function browserExtract<T = unknown>(
  browser: QuickActionBinding,
  input: QuickActionExtractInput
): Promise<T> {
  return quickActionJson<T>(browser, "json", input);
}

/** Retrieve every link on a page via `/links`. */
export function browserLinks(
  browser: QuickActionBinding,
  input: QuickActionPage
): Promise<string[]> {
  return quickActionJson<string[]>(browser, "links", input);
}

/** Scrape specific elements (by CSS selector) via `/scrape`. */
export function browserScrape(
  browser: QuickActionBinding,
  input: QuickActionScrapeInput
): Promise<QuickActionScrapeResult[]> {
  return quickActionJson<QuickActionScrapeResult[]>(browser, "scrape", input);
}

/** Capture rendered HTML plus a base64 screenshot in one request via `/snapshot`. */
export function browserSnapshot(
  browser: QuickActionBinding,
  input: QuickActionPage
): Promise<QuickActionSnapshot> {
  return quickActionJson<QuickActionSnapshot>(browser, "snapshot", input);
}

/**
 * Render a screenshot via `/screenshot`. Returns the raw bytes and their
 * content type — by default a PNG (`image/png`). Note that passing
 * `screenshotOptions.encoding: "base64"` makes the service return a `text/plain`
 * data-URI string instead of binary, which this helper hands back verbatim.
 */
export function browserScreenshot(
  browser: QuickActionBinding,
  input: QuickActionScreenshotInput
): Promise<QuickActionBinary> {
  return quickActionBinary(browser, "screenshot", input);
}

/** Render a PDF via `/pdf`. Returns the PDF bytes. */
export function browserPdf(
  browser: QuickActionBinding,
  input: QuickActionPage
): Promise<QuickActionBinary> {
  return quickActionBinary(browser, "pdf", input);
}
